import React, { useRef, useEffect } from 'react';
import useGPT4Transcription from './useGPT4Transcription';

const SpeechToTextComponent: React.FC = () => {
  // Get API key from environment variable - customize this as needed for your app
  const apiKey = process.env.REACT_APP_OPENAI_STT_KEY || '';
  
  // You can customize these settings as needed
  const language = 'en'; // Default language
  const silenceDuration = 50; // ms
  const vadThreshold = 0.25; // voice activity detection threshold
  const transcriptContainerRef = useRef<HTMLDivElement>(null);

  const {
    isListening,
    isProcessing,
    isMicAvailable,
    isMicPermissionGranted,
    connectionStatus,
    partialTranscript,
    fullTranscript,
    errorMessage,
    statusMessage,
    toggleListening,
    clearTranscript,
    clearFinalTranscript,
  } = useGPT4Transcription({
    apiKey,
    language,
    vadThreshold: vadThreshold,
    prefixPadding: 30,
    silenceDuration: silenceDuration
  });

  // Auto-scroll transcript container to bottom when content changes
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [fullTranscript, partialTranscript]);

  // Optional: Add handlers for when you want to do something with the transcript
  useEffect(() => {
    // You can handle transcript changes here if needed
    // Example: save to state, send to backend, etc.
  }, [fullTranscript]);

  // Optional: Handle status changes
  useEffect(() => {
    // You can handle status changes here if needed
    console.log('Status:', statusMessage);
  }, [statusMessage]);

  // Determine button text based on current state
  const getButtonText = () => {
    if (!isMicAvailable) return "Microphone not available";
    if (!isMicPermissionGranted && !isListening) return "Start Listening";
    if (isListening) return "Stop Listening";
    return "Start Listening";
  };

  // Determine button status/disabled state
  const isButtonDisabled = !isMicAvailable || (errorMessage && !isListening);

  // API key check at the start
  if (!apiKey) {
    return (
      <div style={{ 
        width: '100%', 
        maxWidth: '800px', 
        margin: '0 auto', 
        padding: '16px',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{
          padding: '16px',
          backgroundColor: '#ffebee',
          borderRadius: '4px',
          color: '#b71c1c',
          textAlign: 'center'
        }}>
          <h3>API Key Missing</h3>
          <p>Please set your OpenAI API key in the environment variable REACT_APP_OPENAI_STT_KEY</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: '800px', 
      margin: '0 auto', 
      padding: '16px',
      fontFamily: 'Arial, sans-serif' 
    }}>
      {/* Transcript display */}
      <div
        ref={transcriptContainerRef}
        style={{
          padding: '24px',
          minHeight: '300px',
          maxHeight: '500px',
          marginBottom: '16px',
          overflowY: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: isProcessing ? 'rgba(144, 202, 249, 0.08)' : 'white',
          transition: 'background-color 0.3s ease',
          position: 'relative',
          lineHeight: '1.8',
          whiteSpace: 'pre-wrap'
        }}
      >
        {fullTranscript}
        <span style={{ 
          color: '#666', 
          fontStyle: 'italic' 
        }}>
          {partialTranscript}
        </span>

        {isProcessing && (
          <span style={{
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#1976d2',
            marginLeft: '4px',
            verticalAlign: 'middle',
            animation: 'pulse 1.5s infinite ease-in-out'
          }} />
        )}
      </div>

      {/* Status message display */}
      <div style={{
        padding: '12px',
        marginBottom: '16px',
        backgroundColor: errorMessage ? '#ffebee' : '#e3f2fd',
        borderRadius: '4px',
        color: errorMessage ? '#b71c1c' : '#0d47a1',
        fontSize: '14px'
      }}>
        {statusMessage}
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <button
          onClick={toggleListening}
          disabled={isButtonDisabled}
          style={{
            padding: '10px 16px',
            backgroundColor: isListening ? '#f44336' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
            opacity: isButtonDisabled ? 0.7 : 1,
            fontWeight: 'bold',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isListening ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="6" y="6" width="12" height="12" fill="white" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 15C13.66 15 15 13.66 15 12V6C15 4.34 13.66 3 12 3C10.34 3 9 4.34 9 6V12C9 13.66 10.34 15 12 15Z" fill="white" />
              <path d="M17 12C17 14.76 14.76 17 12 17C9.24 17 7 14.76 7 12H5C5 15.53 7.61 18.43 11 18.93V22H13V18.93C16.39 18.43 19 15.53 19 12H17Z" fill="white" />
            </svg>
          )}
          {getButtonText()}
        </button>

        <button
          onClick={clearTranscript}
          disabled={!fullTranscript && !partialTranscript}
          style={{
            padding: '10px 16px',
            backgroundColor: '#9e9e9e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: !fullTranscript && !partialTranscript ? 'not-allowed' : 'pointer',
            opacity: !fullTranscript && !partialTranscript ? 0.7 : 1
          }}
        >
          Clear Transcript
        </button>
      </div>

      {/* CSS for animations */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 0.4; }
            50% { opacity: 1; }
            100% { opacity: 0.4; }
          }
        `}
      </style>
    </div>
  );
};

export default SpeechToTextComponent;